import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation, useParams, useSearchParams } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { Plus } from 'lucide-react';
import { useAppSelector } from '../../hooks';
import { flashcardActions } from '../../redux/actions/flashcard.actions';
import { FolderCard } from './FolderCard';
import { FolderModal } from './FolderModal';
import { SetEditorSimple } from './SetEditorSimple';
import { SetCard as SetItemCard } from './SetCard';

interface RootState {
  flashcard: {
    folders: any[];
    sets: any[];
    loading: boolean;
    error: string | null;
  };
}

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const Container = styled.div`
  padding: 20px;
  max-width: 1400px;
  margin: 0 auto;
  animation: ${fadeIn} 0.5s ease-out;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  gap: 16px;
  flex-wrap: wrap;
  min-height: 50px;
`;

const Title = styled.h1`
  font-size: 26px;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
`;

const StudentBackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: white;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  color: #6b7280;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #f9fafb;
    border-color: #9ca3af;
    color: #1f2937;
  }

  &:active {
    transform: translateY(0);
  }
`;

const ViewToggle = styled.div`
  display: flex;
  gap: 4px;
  background: #f3f4f6;
  padding: 4px;
  border-radius: 10px;
`;

const ToggleButton = styled.button<{ $active: boolean }>`
  padding: 10px 20px;
  background: ${props => props.$active ? 'white' : 'transparent'};
  color: ${props => props.$active ? '#1f2937' : '#6b7280'};
  border: none;
  border-radius: 8px;
  font-weight: 600;
  font-size: 15px;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: ${props => props.$active ? '0 2px 4px rgba(0, 0, 0, 0.1)' : 'none'};

  &:hover {
    background: ${props => props.$active ? 'white' : '#e5e7eb'};
  }
`;

const CreateButton = styled.button`
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const FolderList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 32px;
`;

const SearchBar = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 20px;
  max-width: 420px;
`;

const SearchInput = styled.input`
  flex: 1;
  padding: 10px 16px 10px 40px;
  border: 2px solid #e5e7eb;
  border-radius: 10px;
  font-size: 14px;
  color: #1f2937;
  background: white url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' fill='%239ca3af' viewBox='0 0 16 16'%3E%3Cpath d='M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398l3.85 3.85a1 1 0 0 0 1.415-1.415l-3.868-3.833zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0z'/%3E%3C/svg%3E") 14px center no-repeat;
  transition: border-color 0.2s;
  outline: none;
  &:focus {
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59,130,246,0.15);
  }
  &::placeholder { color: #9ca3af; }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #6b7280;

  h3 {
    font-size: 18px;
    font-weight: 600;
    color: #1f2937;
    margin: 0 0 8px 0;
  }

  p {
    margin: 0;
    font-size: 14px;
  }
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 60px 20px;

  &::after {
    content: '';
    width: 40px;
    height: 40px;
    border: 4px solid #e5e7eb;
    border-top-color: #3b82f6;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

interface SelectedFolder {
  id: number;
  name: string;
  description?: string;
  color: string;
  icon: string;
  visibility: 'public' | 'private' | 'class';
  sets?: any[];
}

export const FlashcardDashboard: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { folderId } = useParams<{ folderId?: string }>();
  const [searchParams] = useSearchParams();
  const flashcardState = useSelector((state: RootState) => state.flashcard);
  const currentUser = useAppSelector((state: any) => state.authentication?.userInfo);
  const folders = Array.isArray(flashcardState?.folders) ? flashcardState.folders : [];
  const sets = Array.isArray(flashcardState?.sets) ? flashcardState.sets : [];
  const loading = flashcardState?.loading || false;

  // Derive state from URL
  const viewParam = searchParams.get('view');
  const tabParam = searchParams.get('tab');
  const viewMode = viewParam === 'createSet' ? 'createSet' : folderId ? 'detail' : 'list';
  const listMode = tabParam === 'folders' ? 'folders' : 'sets';

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedFolder, setSelectedFolder] = useState<SelectedFolder | null>(null);
  const [selectedSetId, setSelectedSetId] = useState<number | null>(null);
  const [prefilledFolderId, setPrefilledFolderId] = useState<number | null>(null);
  const [setEditorMode, setSetEditorMode] = useState<'create' | 'edit'>('create');
  const [editSetId, setEditSetId] = useState<number | null>(null);
  const [studentModeOnMount] = useState(() => location.pathname.startsWith('/student'));
  const [searchTerm, setSearchTerm] = useState('');

  // Check if accessed from student page - lock in the value from initial mount
  const isStudentMode = studentModeOnMount;

  useEffect(() => {
    dispatch(flashcardActions.getAllFolders() as any);
    dispatch(flashcardActions.getAllSets() as any);
  }, [dispatch]);

  // Load folder detail when folderId changes
  useEffect(() => {
    if (folderId) {
      const folder = folders.find((f: any) => f.id === Number(folderId));
      if (folder) {
        setSelectedFolder(folder);
      }
    } else {
      setSelectedFolder(null);
    }
  }, [folderId, folders]);

  const handleCreateFolder = () => {
    setSelectedFolder(null);
    setIsModalOpen(true);
  };

  const handleCreateSet = () => {
    setPrefilledFolderId(null);
    setSetEditorMode('create');
    setEditSetId(null);
    setSelectedSetId(null);
    const baseUrl = isStudentMode ? '/student/flashcards' : '/flashcards';
    navigate(`${baseUrl}?view=createSet&tab=${listMode}`);
  };

  const handleEditFolder = (folder: any) => {
    setSelectedFolder(folder);
    setIsModalOpen(true);
  };

  const handleDeleteFolder = (folderId: number) => {
    if (window.confirm('Bạn có chắc chắn muốn xóa folder này?')) {
      dispatch(flashcardActions.deleteFolder(folderId) as any);
    }
  };

  const handleSelectFolder = (folder: any) => {
    const baseUrl = isStudentMode ? '/student/flashcards' : '/flashcards';
    navigate(`${baseUrl}/folders/${folder.id}`);
  };

  const handleSelectSet = (setId: number) => {
    const baseUrl = isStudentMode ? '/student/flashcards' : '/flashcards';
    navigate(`${baseUrl}/sets/${setId}/detail`);
  };

  const handleEditSet = (set: any) => {
    const baseUrl = isStudentMode ? '/student/flashcards' : '/flashcards';
    navigate(`${baseUrl}/sets/${set.id}/edit`);
  };

  const handleDeleteSet = (setId: number) => {
    if (window.confirm('Bạn có chắc chắn muốn xóa set này?')) {
      dispatch(flashcardActions.deleteSet(setId) as any);
    }
  };

  const handleBackToList = () => {
    const baseUrl = isStudentMode ? '/student/flashcards' : '/flashcards';
    if (folderId) {
      // Coming from folder detail, go back to folders tab
      navigate(`${baseUrl}?tab=folders`);
    } else {
      // Coming from createSet, go back to previous tab
      navigate(`${baseUrl}?tab=${listMode}`);
    }
    setPrefilledFolderId(null);
    setSetEditorMode('create');
    setEditSetId(null);
  };

  const handleSetCreated = (setId: number) => {
    console.log('Set created with ID:', setId);
    // Reload data and navigate to set detail view
    dispatch(flashcardActions.getAllSets() as any);
    dispatch(flashcardActions.getCardsBySet(setId) as any);
    navigate(`/flashcards/sets/${setId}/detail`);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedFolder(null);
  };

  const handleModalSuccess = () => {
    dispatch(flashcardActions.getAllFolders() as any);
    handleModalClose();
  };

  // Render set editor view
  if (viewMode === 'createSet') {
    return (
      <Container>
        <SetEditorSimple
          onBack={handleBackToList}
          onSuccess={handleSetCreated}
          prefilledFolderId={prefilledFolderId}
          mode={setEditorMode}
          editSetId={editSetId}
        />
      </Container>
    );
  }

  // Render folder detail view
  if (viewMode === 'detail' && selectedFolder) {
    const baseUrl = isStudentMode ? '/student/flashcards' : '/flashcards';
    return (
      <Container>
        <FolderDetailView
          folder={selectedFolder}
          onBack={handleBackToList}
          isStudentMode={isStudentMode}
          onCreateSet={(folderId) => {
            navigate(`${baseUrl}?view=createSet&tab=folders&folderId=${folderId}`);
          }}
          onViewSet={(setId) => {
            navigate(`${baseUrl}/sets/${setId}/detail?from=folder&folderId=${selectedFolder.id}`);
          }}
          onEditSet={(setId) => {
            navigate(`${baseUrl}/sets/${setId}/edit`);
          }}
        />
      </Container>
    );
  }

  // Render folder list view
  return (
    <Container>
      <Header>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', minWidth: 'fit-content' }}>
          {isStudentMode && (
            <StudentBackButton onClick={() => navigate('/student')}>
              ← Quay lại
            </StudentBackButton>
          )}
          <ViewToggle>
            <ToggleButton
              $active={listMode === 'folders'}
              onClick={() => {
                const baseUrl = isStudentMode ? '/student/flashcards' : '/flashcards';
                navigate(`${baseUrl}?tab=folders`);
                setSearchTerm('');
              }}
            >
              📁 Folders
            </ToggleButton>
            <ToggleButton
              $active={listMode === 'sets'}
              onClick={() => {
                const baseUrl = isStudentMode ? '/student/flashcards' : '/flashcards';
                navigate(`${baseUrl}?tab=sets`);
                setSearchTerm('');
              }}
            >
              📚 Bộ từ
            </ToggleButton>
          </ViewToggle>
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          <CreateButton onClick={handleCreateSet} style={{ background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)' }}>
            <Plus /> Tạo Set Từ
          </CreateButton>
          <CreateButton onClick={handleCreateFolder}>
            <Plus /> Tạo Folder
          </CreateButton>
        </div>
      </Header>

      {loading && <LoadingSpinner />}

      {!loading && (
        <SearchBar>
          <SearchInput
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            placeholder={listMode === 'folders' ? '🔍 Tìm folder...' : '🔍 Tìm bộ từ...'}
          />
          {searchTerm && (
            <button onClick={() => setSearchTerm('')}
              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: '#9ca3af', lineHeight: 1 }}
              title="Xóa tìm kiếm">✕</button>
          )}
        </SearchBar>
      )}

      {!loading && listMode === 'folders' && folders.length === 0 && (
        <EmptyState>
          <h3>Chưa có folder nào</h3>
          <p>Hãy tạo folder đầu tiên để bắt đầu học từ vựng</p>
        </EmptyState>
      )}

      {!loading && listMode === 'sets' && sets.length === 0 && (
        <EmptyState>
          <h3>Chưa có set từ nào</h3>
          <p>Hãy tạo set từ đầu tiên để bắt đầu học</p>
        </EmptyState>
      )}

      {!loading && listMode === 'folders' && folders.length > 0 && (() => {
        const filtered = folders.filter((f: any) =>
          f.name.toLowerCase().includes(searchTerm.toLowerCase())
        );
        return filtered.length === 0 ? (
          <EmptyState><h3>Không tìm thấy folder nào</h3><p>Thử từ khóa khác nhé!</p></EmptyState>
        ) : (
          <FolderList>
            {filtered.map((folder: any) => (
              <FolderCard
                key={folder.id}
                id={folder.id}
                name={folder.name}
                icon={folder.icon}
                color={folder.color}
                description={folder.description}
                totalSets={folder.sets ? folder.sets.length : 0}
                createdBy={folder.created_by}
                creatorName={folder.creator?.userName}
                currentUserId={currentUser?.id}
                currentUserRole={currentUser?.role}
                onSelect={() => handleSelectFolder(folder)}
                onEdit={() => handleEditFolder(folder)}
                onDelete={() => handleDeleteFolder(folder.id)}
              />
            ))}
          </FolderList>
        );
      })()}

      {!loading && listMode === 'sets' && sets.length > 0 && (() => {
        const filtered = sets.filter((s: any) =>
          s.name.toLowerCase().includes(searchTerm.toLowerCase())
        );
        return filtered.length === 0 ? (
          <EmptyState><h3>Không tìm thấy bộ từ nào</h3><p>Thử từ khóa khác nhé!</p></EmptyState>
        ) : (
          <FolderList>
            {filtered.map((set: any) => {
              const totalCards = set?.total_cards ?? set?.card_count ?? (Array.isArray(set?.cards) ? set.cards.length : 0) ?? 0;
              const parentFolder = folders.find((f: any) => f.id === set.folder_id);
              return (
                <SetItemCard
                  key={set.id}
                  id={set.id}
                  name={set.name}
                  description={set.description}
                  totalCards={totalCards}
                  folderName={parentFolder?.name}
                  createdBy={set.created_by}
                  creatorName={set.creator?.userName}
                  currentUserId={currentUser?.id}
                  currentUserRole={currentUser?.role}
                  onSelect={() => handleSelectSet(set.id)}
                  onEdit={() => handleEditSet(set)}
                  onDelete={() => handleDeleteSet(set.id)}
                />
              );
            })}
          </FolderList>
        );
      })()}

      {/* Modal tạo/sửa folder */}
      <FolderModal
        isOpen={isModalOpen}
        folder={selectedFolder}
        onClose={handleModalClose}
        onSuccess={handleModalSuccess}
      />
    </Container>
  );
};

// Component chi tiết folder (full page view)
const DetailHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 2px solid #e5e7eb;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: white;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  color: #6b7280;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #f9fafb;
    border-color: #9ca3af;
    color: #1f2937;
  }
`;

const FolderHeaderInfo = styled.div<{ color: string }>`
  flex: 1;
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px 20px;
  background: ${props => props.color}10;
  border: 2px solid ${props => props.color}40;
  border-radius: 12px;
`;

const FolderIcon = styled.div`
  font-size: 48px;
`;

const FolderHeaderText = styled.div`
  flex: 1;
`;

const FolderTitle = styled.h2`
  margin: 0 0 4px 0;
  font-size: 24px;
  font-weight: 700;
  color: #1f2937;
`;

const FolderDescription = styled.p`
  margin: 0;
  font-size: 14px;
  color: #6b7280;
`;

const SetSection = styled.div`
  margin-top: 24px;
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

const SectionTitle = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: #1f2937;
`;

const SetList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SetRowCard = styled.div<{ color: string }>`
  padding: 16px 20px;
  background: white;
  border: 2px solid #e5e7eb;
  border-left: 4px solid ${props => props.color};
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  transition: all 0.2s;

  &:hover {
    transform: translateX(4px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    border-color: ${props => props.color};
  }
`;

const SetInfo = styled.div`
  flex: 1;
`;

const SetName = styled.h4`
  margin: 0 0 4px 0;
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
`;

const SetMeta = styled.div`
  font-size: 13px;
  color: #9ca3af;
`;

const SetActions = styled.div`
  display: flex;
  gap: 8px;
`;

const SetActionButton = styled.button`
  padding: 8px 12px;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  color: #6b7280;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #f9fafb;
    border-color: #9ca3af;
    color: #1f2937;
  }
`;

interface FolderDetailViewProps {
  folder: SelectedFolder;
  onBack: () => void;
  onCreateSet: (folderId: number) => void;
  onViewSet: (setId: number) => void;
  onEditSet: (setId: number) => void;
  isStudentMode?: boolean;
}

const FolderDetailView: React.FC<FolderDetailViewProps> = ({ folder, onBack, onCreateSet, onViewSet, onEditSet, isStudentMode }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const allSets = useSelector((state: RootState) => (state.flashcard as any).sets || []);

  const folderSets = allSets.filter((s: any) => s.folder_id === folder.id);

  const handleCreateSet = () => {
    onCreateSet(folder.id);
  };

  const handleDeleteSet = (setId: number) => {
    if (window.confirm('Bạn có chắc chắn muốn xóa set này?')) {
      dispatch(flashcardActions.deleteSet(setId) as any);
    }
  };

  return (
    <>
      <DetailHeader>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          <BackButton onClick={onBack}>
            ← Quay lại
          </BackButton>
        </div>
        <FolderHeaderInfo color={folder.color}>
          <FolderIcon>{folder.icon}</FolderIcon>
          <FolderHeaderText>
            <FolderTitle>{folder.name}</FolderTitle>
            {folder.description && (
              <FolderDescription>{folder.description}</FolderDescription>
            )}
          </FolderHeaderText>
        </FolderHeaderInfo>
      </DetailHeader>

      <SetSection>
        <SectionHeader>
          <SectionTitle>📚 Danh sách Set Từ</SectionTitle>
          <CreateButton onClick={handleCreateSet}>
            <Plus /> Tạo Set Mới
          </CreateButton>
        </SectionHeader>

        {folderSets.length === 0 ? (
          <EmptyState>
            <h3>Chưa có set nào</h3>
            <p>Hãy tạo set đầu tiên để bắt đầu thêm từ vựng</p>
          </EmptyState>
        ) : (
          <SetList>
            {folderSets.map((set: any) => (
              <SetRowCard
                key={set.id}
                color={folder.color}
                onClick={() => onViewSet(set.id)}
                style={{ cursor: 'pointer' }}
              >
                <SetInfo>
                  <SetName>{set.name}</SetName>
                  <SetMeta>
                    {(() => {
                      const totalCards = set?.total_cards ?? set?.card_count ?? (Array.isArray(set?.cards) ? set.cards.length : 0) ?? 0;
                      return `📇 ${totalCards} thẻ • ${set.language_from} → ${set.language_to}${set.difficulty ? ` • ${set.difficulty}` : ''}`;
                    })()}
                  </SetMeta>
                </SetInfo>
                <SetActions>
                  {!isStudentMode && (
                    <>
                      <SetActionButton onClick={(e) => {
                        e.stopPropagation();
                        onEditSet(set.id);
                      }}>
                        ✏️ Sửa
                      </SetActionButton>
                      <SetActionButton onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteSet(set.id);
                      }}>
                        🗑️ Xóa
                      </SetActionButton>
                    </>
                  )}
                </SetActions>
              </SetRowCard>
            ))}
          </SetList>
        )}
      </SetSection>
    </>
  );
};

export default FlashcardDashboard;
